import { useTranslation } from 'react-i18next'

import { KpiTile } from '@/components/data/KpiTile'
import { isZeroAmount } from '@/features/counterparties/api/counterpartyMoneyWire'
import { formatMoney, formatNumber, splitMoney } from '@/lib/format'
import type { CounterpartyStatementTotals, Money } from '@/types/domain'

/** The counterparty's year as four tiles (S-22e, `direction-ac2-final.png`
 *  with the owner's 2026-10-23 changes): Перевозки · Начислено · Оплачено ·
 *  Остаток к оплате — one USD figure each. Every figure is SERVED, never summed
 *  here; «Остаток к оплате» is `totals.balance`, the closing running balance
 *  (the opening plus the year's «Остаток»), as the statement's last row reads.
 *
 *  Under them, two lines that qualify the figures rather than add to them:
 *  the advance — an outbound payment to the counterparty not yet allocated,
 *  which is in neither «Оплачено» nor «Остаток» (AD-11) — when there is one,
 *  and «Цифры по вашим работам» when the reader's money is their own works. */
export function CounterpartyMoneyTiles({
  totals,
  scoped,
}: {
  totals: CounterpartyStatementTotals
  scoped: boolean
}) {
  const { t } = useTranslation()
  const tile = (label: string, value: Money) => {
    const { amount, currency } = splitMoney(value.amount, value.currency)
    return <KpiTile label={label} value={amount} unit={currency} />
  }
  const advance = isZeroAmount(totals.advance.amount) ? undefined : totals.advance

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile label={t('counterparties.money.tiles.units')} value={formatNumber(totals.units)} />
        {tile(t('counterparties.money.tiles.billed'), totals.billed)}
        {tile(t('counterparties.money.tiles.paid'), totals.paid)}
        {tile(t('counterparties.money.tiles.balance'), totals.balance)}
      </div>
      {advance || scoped ? (
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 px-1 text-[12.5px]">
          {advance ? (
            <span className="text-text-heading" data-testid="counterparty-advance">
              {/* Tabular figures on the amount only: on the label they space
                  out its comma and colon. */}
              {t('counterparties.money.advance')}{' '}
              <span className="asa-num">{formatMoney(advance.amount, advance.currency)}</span>
            </span>
          ) : null}
          {scoped ? (
            <span className="text-text-muted font-light">{t('counterparties.money.scoped')}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
